import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../USER/Auth/AuthContext';
import { getDriverServicePreferences, setDriverServicePreference } from '../../../../services/api/trips';
import BrySwitch from '../../../UI/BryUI/BrySwitch';
import { Filter, ChevronDown, ChevronUp } from 'lucide-react';
import Swal from 'sweetalert2';

const DriverServicePreferencesCard = ({ onPreferencesChange }) => {
  const { currentUser, isDriver } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadPreferences() {
      if (!currentUser?.id || !isDriver) return;
      try {
        setLoading(true);
        const prefs = await getDriverServicePreferences(currentUser.id);
        if (isMounted) setCategories(prefs);
      } catch (err) {
        console.error('Error cargando preferencias de servicios del conductor:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadPreferences();

    return () => {
      isMounted = false;
    };
  }, [currentUser?.id, isDriver]);

  if (!isDriver || (!loading && categories.length === 0)) return null;

  const handleToggle = async (cat) => {
    const newStatus = !cat.is_enabled;

    // Validación: al menos una categoría debe quedar activa
    const activeCount = categories.filter((c) => c.is_enabled).length;
    if (!newStatus && activeCount <= 1) {
      Swal.fire({
        icon: 'warning',
        title: 'Acción no permitida',
        text: 'Debes mantener activa al menos una categoría de servicio para seguir recibiendo solicitudes.',
        background: '#1F2937',
        color: 'var(--bry-text-main, #fff)',
        confirmButtonColor: 'var(--bry-accent, #0EABB0)',
      });
      return;
    }

    // Actualización optimista
    setCategories((prev) =>
      prev.map((c) => (c.id === cat.id ? { ...c, is_enabled: newStatus } : c))
    );
    setSavingId(cat.id);

    try {
      await setDriverServicePreference(currentUser.id, cat.id, newStatus);
      if (onPreferencesChange) {
        onPreferencesChange(cat.id, newStatus);
      }
    } catch (err) {
      console.error('Error guardando preferencia de servicio:', err);
      // Revertir en caso de error
      setCategories((prev) =>
        prev.map((c) => (c.id === cat.id ? { ...c, is_enabled: !newStatus } : c))
      );
      Swal.fire({
        icon: 'error',
        title: 'Error al actualizar',
        text: 'No se pudo guardar la preferencia. Verifica tu conexión a internet.',
        background: '#1F2937',
        color: 'var(--bry-text-main, #fff)',
      });
    } finally {
      setSavingId(null);
    }
  };

  const activeCount = categories.filter((c) => c.is_enabled).length;

  return (
    <div
      className="rounded-3 p-3 mb-3 shadow-sm border border-secondary border-opacity-25"
      style={{ backgroundColor: '#111827', color: 'var(--bry-text-main, #f3f4f6)' }}
    >
      <div
        className="d-flex align-items-center justify-content-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: 34, height: 34, backgroundColor: 'rgba(14, 171, 176, 0.15)', color: 'var(--bry-accent, #0EABB0)' }}
          >
            <Filter size={18} />
          </div>
          <div>
            <div className="fw-semibold" style={{ fontSize: '0.95rem' }}>
              Servicios que Aceptas
            </div>
            <div className="text-secondary" style={{ fontSize: '0.8rem' }}>
              {activeCount} de {categories.length} categorías activas en tu radar
            </div>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-sm text-secondary p-1 border-0"
          aria-label="Alternar panel de preferencias de servicio"
        >
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {isExpanded && (
        <div className="mt-3 pt-3 border-top border-secondary border-opacity-25 d-flex flex-column gap-2">
          {loading ? (
            <div className="text-secondary small text-center py-2">Cargando categorías...</div>
          ) : (
            categories.map((cat) => (
              <div
                key={cat.id}
                className="d-flex align-items-center justify-content-between p-2 rounded-2"
                style={{ backgroundColor: '#1f2937' }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '1.25rem' }}>{cat.icon || '🚀'}</span>
                  <div>
                    <div className="fw-medium" style={{ fontSize: '0.9rem' }}>
                      {cat.name}
                    </div>
                    {cat.description && (
                      <div className="text-secondary" style={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                        {cat.description}
                      </div>
                    )}
                  </div>
                </div>

                <BrySwitch
                  checked={cat.is_enabled}
                  onChange={() => handleToggle(cat)}
                  disabled={savingId === cat.id}
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default DriverServicePreferencesCard;
